import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

export default function TermsOfService() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen" data-testid="terms-of-service-page">
      <Helmet>
        <title>Terms of Service - Trademark Venus | Service Agreement & Policies</title>
        <meta name="description" content="Review Trademark Venus's terms of service outlining our trademark registration services, fees, client responsibilities, and service agreements." />
      </Helmet>
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/8112131/pexels-photo-8112131.jpeg"
            alt="Hero background"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-red-900 opacity-90" />
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl md:text-6xl font-heading font-bold text-white leading-tight mb-12">
              Terms of Service
            </h1>

            <div className="prose prose-slate max-w-none">
              <div className="space-y-8">
                <section>
                  <h2 className="text-2xl font-heading font-bold text-white mb-4">
                    1. Acceptance of Terms
                  </h2>
                  <p className="text-white leading-relaxed">
                    By accessing and using Trademark Venus's services, you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-heading font-bold text-white mb-4">
                    2. Services Provided
                  </h2>
                  <p className="text-white leading-relaxed mb-4">
                    Trademark Venus provides trademark registration services, copyright registration, trademark searches, company formation, and related legal services. Our services include:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-white">
                    <li>Preparation and filing of trademark applications</li>
                    <li>Comprehensive trademark searches</li>
                    <li>Copyright registration services</li>
                    <li>Company formation assistance</li>
                    <li>Amazon Brand Registry enrollment</li>
                    <li>Ongoing monitoring and support</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-heading font-bold text-white mb-4">
                    3. Client Responsibilities
                  </h2>
                  <p className="text-white leading-relaxed mb-4">
                    As a client, you agree to:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-white">
                    <li>Provide accurate and complete information</li>
                    <li>Respond to our requests for information in a timely manner</li>
                    <li>Pay all fees and government filing fees as agreed</li>
                    <li>Comply with all applicable laws and regulations</li>
                    <li>Not use our services for any unlawful purpose</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-heading font-bold text-white mb-4">
                    4. Fees and Payment
                  </h2>
                  <p className="text-white leading-relaxed mb-4">
                    Service fees are as stated on our website at the time of purchase. Government filing fees are separate and subject to change by the relevant government agencies. Payment terms include:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-white">
                    <li>Service fees are due upon engagement</li>
                    <li>Government filing fees must be paid before filing</li>
                    <li>All fees are non-refundable except as required by law</li>
                    <li>Prices are subject to change without notice</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-heading font-bold text-white mb-4">
                    5. No Guarantee of Registration
                  </h2>
                  <p className="text-white leading-relaxed">
                    While we work diligently to prepare and file your applications properly, we cannot guarantee that the USPTO or any other government agency will approve your trademark registration. Final decisions rest with the examining authorities.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-heading font-bold text-white mb-4">
                    6. Attorney-Client Relationship
                  </h2>
                  <p className="text-white leading-relaxed">
                    Use of our services does not create an attorney-client relationship unless explicitly stated in a separate engagement agreement. Our services are provided for informational and filing purposes.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-heading font-bold text-white mb-4">
                    7. Limitation of Liability
                  </h2>
                  <p className="text-white leading-relaxed">
                    Trademark Venus shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use or inability to use our services. Our total liability shall not exceed the amount paid for our services.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-heading font-bold text-white mb-4">
                    8. Intellectual Property
                  </h2>
                  <p className="text-white leading-relaxed">
                    All content on our website, including text, graphics, logos, and software, is the property of Trademark Venus and protected by copyright and trademark laws. You may not reproduce, distribute, or create derivative works without our express permission.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-heading font-bold text-white mb-4">
                    9. Termination
                  </h2>
                  <p className="text-white leading-relaxed">
                    We reserve the right to terminate or suspend your access to our services at any time for violation of these terms or for any other reason at our discretion.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-heading font-bold text-white mb-4">
                    10. Governing Law
                  </h2>
                  <p className="text-white leading-relaxed">
                    These Terms of Service shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-heading font-bold text-white mb-4">
                    11. Changes to Terms
                  </h2>
                  <p className="text-white leading-relaxed">
                    We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting to our website. Your continued use of our services constitutes acceptance of the modified terms.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-heading font-bold text-white mb-4">
                    12. Contact Information
                  </h2>
                  <p className="text-white leading-relaxed">
                    For questions about these Terms of Service, please contact us at info@trademarkvenus.com.
                  </p>
                </section>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
